import { useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { Button } from '@/components/ui/button'
import { X } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useOnboarding } from '@/contexts/OnboardingContext'
import { OnboardingStepper } from './OnboardingStepper'
import { ONBOARDING_STEPS_CONFIG, PROFESSIONAL_ONBOARDING_STEPS_CONFIG } from './types'
import {
  WelcomeStep,
  IntegrationsStep,
  AvailabilityStep,
  PackagesStep,
  ContractsStep,
  ReviewStep,
} from './steps'
import {
  ProfessionalWelcomeStep,
  ProfessionalProfileStep,
  ProfessionalServicesStep,
  ProfessionalAvailabilityStep,
  ProfessionalReviewStep,
} from './steps/professional'

export function OnboardingModal() {
  const { t } = useTranslation('onboarding')
  const {
    isOpen,
    isLoading,
    flow,
    steps,
    currentStep,
    currentStepIndex,
    status,
    closeOnboarding,
    goToStep,
    nextStep,
    prevStep,
    completeOnboarding,
    skipOnboarding,
    refreshStatus,
  } = useOnboarding()

  const stepsConfig = flow === 'professional' ? PROFESSIONAL_ONBOARDING_STEPS_CONFIG : ONBOARDING_STEPS_CONFIG

  const handleStepClick = useCallback(
    (index: number) => {
      if (index <= currentStepIndex) {
        goToStep(steps[index])
      }
    },
    [currentStepIndex, goToStep, steps]
  )

  const handleSkipStep = useCallback(() => {
    nextStep()
  }, [nextStep])

  const handleSkipOnboarding = useCallback(async () => {
    await skipOnboarding()
  }, [skipOnboarding])

  if (isLoading) {
    return null
  }

  const renderProfessionalStep = () => {
    switch (currentStep) {
      case 'welcome':
        return (
          <ProfessionalWelcomeStep
            tenantName={status?.tenantName ?? null}
            onNext={nextStep}
            onSkip={handleSkipOnboarding}
          />
        )
      case 'profile':
        return (
          <ProfessionalProfileStep
            onNext={nextStep}
            onBack={prevStep}
            onSkip={handleSkipStep}
            onRefresh={refreshStatus}
          />
        )
      case 'services':
        return (
          <ProfessionalServicesStep
            onNext={nextStep}
            onBack={prevStep}
            onSkip={handleSkipStep}
          />
        )
      case 'availability':
        return (
          <ProfessionalAvailabilityStep
            onNext={nextStep}
            onBack={prevStep}
            onSkip={handleSkipStep}
            onRefresh={refreshStatus}
          />
        )
      case 'review':
        return (
          <ProfessionalReviewStep
            onBack={prevStep}
            onComplete={completeOnboarding}
            professional={status?.professional ?? null}
          />
        )
      default:
        return null
    }
  }

  const renderStep = () => {
    if (flow === 'professional') {
      return renderProfessionalStep()
    }

    switch (currentStep) {
      case 'welcome':
        return (
          <WelcomeStep
            onNext={nextStep}
            onSkip={handleSkipOnboarding}
          />
        )
      case 'integrations':
        return (
          <IntegrationsStep
            onNext={nextStep}
            onBack={prevStep}
            onSkip={handleSkipStep}
            status={status?.integrations ? { stripe: status.integrations.stripe, calendar: status.integrations.calendar } : null}
            onRefresh={refreshStatus}
          />
        )
      case 'availability':
        return (
          <AvailabilityStep
            onNext={nextStep}
            onBack={prevStep}
            googleConnected={status?.integrations.calendar ?? false}
            availability={status?.availability ?? null}
            onRefresh={refreshStatus}
            onGoToIntegrations={() => goToStep('integrations')}
          />
        )
      case 'packages':
        return (
          <PackagesStep
            onNext={nextStep}
            onBack={prevStep}
            onSkip={handleSkipStep}
            hasPackages={status?.hasPackages ?? false}
            onRefresh={refreshStatus}
          />
        )
      case 'contracts':
        return (
          <ContractsStep
            onNext={nextStep}
            onBack={prevStep}
            onSkip={handleSkipStep}
            hasContractTemplate={status?.hasContractTemplate ?? false}
            onRefresh={refreshStatus}
          />
        )
      case 'review':
        return (
          <ReviewStep
            onBack={prevStep}
            onComplete={completeOnboarding}
            status={status?.integrations ? { stripe: status.integrations.stripe, calendar: status.integrations.calendar } : null}
            availability={status?.availability ?? null}
            hasPackages={status?.hasPackages ?? false}
            hasContractTemplate={status?.hasContractTemplate ?? false}
          />
        )
      default:
        return null
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeOnboarding()}>
      <DialogContent
        className={cn(
          '!w-[95vw] !max-w-[1400px] sm:!max-w-[1400px] lg:!w-[90vw] xl:!w-[1400px] max-h-[90vh] overflow-hidden p-0',
          'bg-white dark:bg-gray-900',
          'rounded-2xl shadow-2xl',
          'border-0'
        )}
        aria-describedby={undefined}
      >
        <VisuallyHidden>
          <DialogTitle>{t('setupGuide')}</DialogTitle>
        </VisuallyHidden>
        {/* Close button */}
        <Button
          onClick={handleSkipOnboarding}
          variant="ghost"
          size="icon"
          className={cn(
            'absolute top-4 right-4 z-10',
            'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300',
            'rounded-full'
          )}
        >
          <X weight="bold" className="w-5 h-5" />
          <span className="sr-only">{t('close')}</span>
        </Button>

        {/* Main layout */}
        <div className="flex flex-col md:flex-row h-full min-h-[500px] max-h-[80vh]">
          {/* Sidebar with stepper */}
          <div
            className={cn(
              'hidden md:flex md:flex-col',
              'w-64 p-6',
              'bg-[#F6EBDD]/50 dark:bg-gray-800/50',
              'border-r border-gray-200 dark:border-gray-700'
            )}
          >
            {/* Logo/Brand */}
            <div className="mb-8">
              <h3 className="text-lg font-bold text-[#0B2A4A] dark:text-white">
                PinDay
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('setupGuide')}
              </p>
            </div>

            {/* Stepper */}
            <OnboardingStepper
              currentStep={currentStepIndex}
              onStepClick={handleStepClick}
              className="flex-1"
              stepsConfig={stepsConfig}
            />

            {/* Skip link */}
            <div className="mt-auto pt-6">
              <button
                onClick={handleSkipOnboarding}
                className="text-xs text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
              >
                {t('skipSetup')}
              </button>
            </div>
          </div>

          {/* Content area */}
          <div className="flex-1 overflow-y-auto">
            {/* Mobile stepper */}
            <div className="md:hidden px-4 pt-4">
              <OnboardingStepper
                currentStep={currentStepIndex}
                onStepClick={handleStepClick}
                stepsConfig={stepsConfig}
              />
            </div>

            {/* Step content */}
            <div className="p-4 md:p-8">{renderStep()}</div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
